import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';

/**
 * useCollapseAnimation 选项配置。
 *
 * - 只关心折叠状态（collapsed）与内容高度（contentRef.current.scrollHeight）；
 * - 不负责「谁在管理 collapsed」，因此可以在任意已有折叠状态的组件中复用；
 * - 适合单独用在已有业务状态管理的组件里，仅复用高度动画逻辑。
 */
export interface IUseCollapseAnimationOptions {
  collapsed: boolean;
  contentRef?: RefObject<HTMLElement | null>;
  duration?: number;
  enabled?: boolean;
}

/**
 * useCollapseAnimation 返回结果。
 *
 * - maxHeight：用于 CSS 过渡的高度值（0 / 具体高度 / 'none'）；
 * - isAnimating：当前是否处于动画过程，可用于添加过渡中的样式。
 */
export interface IUseCollapseAnimationReturn {
  maxHeight: number | 'none';
  isAnimating: boolean;
}

/**
 * 折叠/展开高度动画 Hook（纯动画层）。
 *
 * 用途：
 * - 根据 collapsed 的变化，自动计算 maxHeight，并在收起/展开时进行平滑动画；
 * - 不关心业务状态管理，可被任意已有 collapsed 状态的组件直接复用。
 */
export function useCollapseAnimation(
  options: IUseCollapseAnimationOptions
): IUseCollapseAnimationReturn {
  const { collapsed, contentRef, duration = 300, enabled = true } = options;

  const isEnabled = enabled && !!contentRef;
  const [maxHeight, setMaxHeight] = useState<number | 'none'>(() =>
    collapsed ? 0 : 'none'
  );
  const [isAnimating, setIsAnimating] = useState(false);

  const isFirstRunRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);

  const cleanupTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const cleanupRaf = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  useEffect(() => {
    cleanupTimer();
    cleanupRaf();

    if (!isEnabled) {
      // 未启用动画时，返回稳定的默认值
      setIsAnimating(false);
      setMaxHeight(collapsed ? 0 : 'none');
      isFirstRunRef.current = false;
      return;
    }

    const el = contentRef?.current;
    if (!el) {
      return;
    }

    // 首次渲染：不触发动画，直接设置到目标状态
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      setIsAnimating(false);
      setMaxHeight(collapsed ? 0 : 'none');
      return;
    }

    if (collapsed) {
      // 收起：先定格当前高度，再下一帧置 0
      const currentHeight = el.scrollHeight;
      setIsAnimating(true);
      setMaxHeight(currentHeight);

      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => {
          setMaxHeight(0);
          timerRef.current = setTimeout(() => {
            setIsAnimating(false);
          }, duration);
        });
      });
    } else {
      // 展开：先设置为内容高度，结束后置为 none
      const targetHeight = el.scrollHeight;
      setIsAnimating(true);
      setMaxHeight(targetHeight);

      timerRef.current = setTimeout(() => {
        setMaxHeight('none');
        setIsAnimating(false);
      }, duration);
    }

    return () => {
      cleanupTimer();
      cleanupRaf();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, duration, isEnabled]);

  return useMemo(
    () => ({
      maxHeight,
      isAnimating,
    }),
    [maxHeight, isAnimating]
  );
}

